import { Input as InputAntd, InputRef } from 'antd'
import { clsx } from 'clsx'
import { useFocus } from 'hook/useFocus'
import { useLatestCallback } from 'hook/useLatestCallback'
import { Icon } from 'icon/Icon'
import { ChangeEvent, CSSProperties, ForwardedRef, forwardRef, KeyboardEvent, type ReactNode } from 'react'
import { opt } from 'store/opt'
import { setRef } from 'util/ref'
import { styles } from 'util/style'
import css from './Input.module.scss'

export type InputProps = {
  after?: ReactNode
  before?: ReactNode
  border?: boolean
  borderless?: boolean
  className?: string
  clear?: boolean
  disabled?: boolean
  error?: boolean
  height?: number
  inputMode?: string
  large?: boolean
  limit?: number
  mono?: boolean
  onChange?(value: string): void
  onFocus?(focus: boolean): void
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void
  panel?: boolean
  placeholder?: string
  readonly?: boolean
  style?: CSSProperties
  upper?: boolean
  value: string
  wide?: boolean
}

export const Input = forwardRef((props: InputProps, ref: ForwardedRef<HTMLInputElement | HTMLElement>) => {
  const {
    after,
    before,
    border,
    borderless,
    clear,
    disabled,
    error,
    height,
    inputMode,
    large,
    limit,
    mono,
    onKeyDown,
    panel,
    placeholder,
    readonly,
    upper,
    value,
    wide,
  } = props

  const focus = useFocus(props)
  const type = border ? css.border : panel ? css.panel : borderless ? css.borderless : opt.tsum ? css.tsum : ''
  const className = clsx(props.className, css.input, type, mono && css.mono, upper && css.upper, wide && css.wide)
  const style = styles({ height, ...props.style })
  const size = large ? 'large' : undefined
  const status = error ? 'error' : undefined
  const clearIcon = <Icon name="close" size={16} />
  const allowClear = clear === false ? undefined : { clearIcon }
  const undeclared = { inputMode } as any

  const onChange = useLatestCallback((event: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(event.target.value)
  })

  const onRef = useLatestCallback((input: InputRef | null) => {
    setRef(ref, input?.input)
  })

  return <InputAntd
    {...undeclared}
    addonAfter={after}
    prefix={before}
    allowClear={allowClear}
    className={className}
    disabled={disabled}
    maxLength={limit}
    onBlur={focus.onBlur}
    onChange={onChange}
    onFocus={focus.onFocus}
    onKeyDown={onKeyDown}
    placeholder={placeholder}
    readOnly={readonly}
    ref={onRef}
    size={size}
    status={status}
    style={style}
    value={value}
  />
})
